/**
 * PropertyMedia - Enhanced Media Display Components
 * 
 * Provides specialized display for:
 * - Virtual tours (iframe embed)
 * - Videos (with player)
 * - Floor plans (with zoom)
 * - Documents
 */

'use client';

import { useState } from 'react';
import Image from 'next/image';
import { 
  Play, 
  Maximize2, 
  X, 
  FileText, 
  Video, 
  Box,
  Download,
  ExternalLink,
} from 'lucide-react';
import type { MediaLink } from '@/lib/api/clients/properties';

// =============================================================================
// UTILITIES
// =============================================================================

/**
 * Grouped media by functional category for UI consumption.
 * Maps from the Route Royal API's MediaUsageContext enum values
 * into UI-friendly groups.
 */
export interface GroupedMedia {
  PRIMARY_HERO: MediaLink[];
  GALLERY: MediaLink[];
  FLOOR_PLAN: MediaLink[];
  VIDEO: MediaLink[];
  VIRTUAL_TOUR: MediaLink[];
  DOCUMENT: MediaLink[];
  OTHER: MediaLink[];
}

const CONTEXT_TO_GROUP: Record<string, keyof GroupedMedia> = {
  PRIMARY_HERO: 'PRIMARY_HERO',
  GALLERY: 'GALLERY',
  LAYOUT_PLAN: 'FLOOR_PLAN',
  VIDEO_WALKTHROUGH: 'VIDEO',
  VIRTUAL_TOUR_360: 'VIRTUAL_TOUR',
  AERIAL: 'GALLERY',
  DOCUMENT: 'DOCUMENT',
};

/**
 * Group media links by usage context.
 * Video files whose context would normally map to GALLERY (e.g. AERIAL drone
 * footage) are routed to the VIDEO group so they render with the video player.
 */
export function groupMediaByContext(mediaLinks: MediaLink[]): GroupedMedia {
  const groups: GroupedMedia = {
    PRIMARY_HERO: [],
    GALLERY: [],
    FLOOR_PLAN: [],
    VIDEO: [],
    VIRTUAL_TOUR: [],
    DOCUMENT: [],
    OTHER: [],
  };
  
  for (const link of mediaLinks) {
    let group = CONTEXT_TO_GROUP[link.usageContext] ?? 'OTHER';

    // Route video files to the VIDEO group even if their context maps elsewhere
    const isVideo = link.media?.mediaType === 'VIDEO' ||
      link.media?.mimeType?.startsWith('video/');
    if (isVideo && group === 'GALLERY') {
      group = 'VIDEO';
    }

    groups[group].push(link);
  }
  
  for (const key of Object.keys(groups) as (keyof GroupedMedia)[]) {
    groups[key].sort((a, b) => a.sortOrder - b.sortOrder);
  }
  
  return groups;
}

// =============================================================================
// VIRTUAL TOUR COMPONENT
// =============================================================================

export interface PropertyVirtualTourProps {
  tours: MediaLink[];
  className?: string;
  messages?: {
    title?: string;
    subtitle?: string;
    viewTour?: string;
    fullscreen?: string;
  };
}

export function PropertyVirtualTour({
  tours,
  className = '',
  messages = {},
}: PropertyVirtualTourProps) {
  const [activeTour, setActiveTour] = useState<MediaLink | null>(null);
  
  const {
    title = 'Virtual Tour',
    subtitle = 'Explore this property in 360°',
    viewTour = 'View Tour',
  } = messages;

  if (tours.length === 0) return null;

  return (
    <section className={className}>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Box className="w-6 h-6 text-primary" />
          {title}
        </h2>
        <p className="text-gray-600 mt-1">{subtitle}</p>
      </div>

      {/* Tour Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {tours.map((tour) => (
          <div
            key={tour.id}
            className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden group cursor-pointer"
            onClick={() => setActiveTour(tour)}
          >
            {tour.media.thumbnailUrl ? (
              <Image
                src={tour.media.thumbnailUrl}
                alt={tour.contextualTitle || 'Virtual Tour'}
                fill
                className="object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5">
                <Box className="w-16 h-16 text-primary/50" />
              </div>
            )}
            
            {/* Overlay */}
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="text-center text-white">
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-2">
                  <Box className="w-8 h-8" />
                </div>
                <p className="font-medium">{viewTour}</p>
              </div>
            </div>
            
            {tour.contextualTitle && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                <p className="text-white font-medium">{tour.contextualTitle}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Fullscreen Modal */}
      {activeTour && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
          <button
            onClick={() => setActiveTour(null)}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="w-full h-full max-w-6xl max-h-[80vh]">
            <iframe
              src={activeTour.media.url}
              className="w-full h-full rounded-lg"
              allowFullScreen
              title={activeTour.contextualTitle || 'Virtual Tour'}
            />
          </div>
        </div>
      )}
    </section>
  );
}

// =============================================================================
// VIDEO COMPONENT
// =============================================================================

/**
 * Attempts to convert a URL to a YouTube/Vimeo embed URL.
 * Returns null if the URL is not a recognised external video platform.
 */
function getEmbedUrl(url: string | undefined | null): string | null {
  if (!url) return null;

  // YouTube: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID
  const ytMatch = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  if (ytMatch) {
    return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&rel=0`;
  }

  // Vimeo: vimeo.com/ID, player.vimeo.com/video/ID
  const vimeoMatch = url.match(
    /(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/
  );
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
  }

  return null;
}

export interface PropertyVideosProps {
  videos: MediaLink[];
  className?: string;
  messages?: {
    title?: string;
    subtitle?: string;
    playVideo?: string;
  };
}

export function PropertyVideos({
  videos,
  className = '',
  messages = {},
}: PropertyVideosProps) {
  const [activeVideo, setActiveVideo] = useState<MediaLink | null>(null);
  
  const {
    title = 'Property Videos',
    subtitle = 'Watch video tours of this property',
  } = messages;

  if (videos.length === 0) return null;

  // Determine embed URL for the active video (YouTube/Vimeo or null for native)
  const activeEmbedUrl = activeVideo
    ? getEmbedUrl(activeVideo.media.externalEmbedUrl) || getEmbedUrl(activeVideo.media.url)
    : null;

  return (
    <section className={className}>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Video className="w-6 h-6 text-primary" />
          {title}
        </h2>
        <p className="text-gray-600 mt-1">{subtitle}</p>
      </div>

      {/* Video Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {videos.map((video) => {
          const thumbUrl = video.media.thumbnailUrl;

          return (
          <div
            key={video.id}
            className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden group cursor-pointer"
            onClick={() => setActiveVideo(video)}
          >
            {thumbUrl ? (
              <Image
                src={thumbUrl}
                alt={video.contextualTitle || 'Property Video'}
                fill
                className="object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-100">
                <Video className="w-12 h-12 text-gray-400" />
              </div>
            )}
            
            {/* Play button overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-black/60 flex items-center justify-center group-hover:bg-primary transition-colors">
                <Play className="w-8 h-8 text-white ml-1" fill="currentColor" />
              </div>
            </div>
            
            {video.contextualTitle && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                <p className="text-white font-medium">{video.contextualTitle}</p>
              </div>
            )}
          </div>
          );
        })}
      </div>

      {/* Video Modal */}
      {activeVideo && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
          <button
            onClick={() => setActiveVideo(null)}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="w-full max-w-4xl aspect-video">
            {activeEmbedUrl ? (
              <iframe
                src={activeEmbedUrl}
                className="w-full h-full rounded-lg"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={activeVideo.contextualTitle || 'Property Video'}
              />
            ) : (
              <video
                src={activeVideo.media.url}
                className="w-full h-full rounded-lg"
                controls
                autoPlay
              />
            )}
          </div>
        </div>
      )}
    </section>
  );
}

// =============================================================================
// FLOOR PLANS COMPONENT
// =============================================================================

export interface PropertyFloorPlansProps {
  floorPlans: MediaLink[];
  className?: string;
  messages?: {
    title?: string;
    subtitle?: string;
    viewFloorPlan?: string;
    download?: string;
  };
}

export function PropertyFloorPlans({
  floorPlans,
  className = '',
  messages = {},
}: PropertyFloorPlansProps) {
  const [activePlan, setActivePlan] = useState<MediaLink | null>(null);
  
  const {
    title = 'Floor Plans',
    subtitle = 'View the layout of this property',
    viewFloorPlan = 'View Floor Plan',
    download = 'Download',
  } = messages;

  if (floorPlans.length === 0) return null;

  return (
    <section className={className}>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Maximize2 className="w-6 h-6 text-primary" />
          {title}
        </h2>
        <p className="text-gray-600 mt-1">{subtitle}</p>
      </div>

      {/* Floor Plan Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {floorPlans.map((plan) => (
          <div
            key={plan.id}
            className="relative bg-white border border-gray-200 rounded-lg overflow-hidden group"
          >
            <div 
              className="aspect-[4/3] relative cursor-pointer"
              onClick={() => setActivePlan(plan)}
            >
              <Image
                src={plan.media.url}
                alt={plan.contextualTitle || 'Floor Plan'}
                fill
                className="object-contain p-4"
              />
              
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="text-center text-white">
                  <Maximize2 className="w-8 h-8 mx-auto mb-2" />
                  <p className="font-medium">{viewFloorPlan}</p>
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="flex items-center justify-between p-3 border-t border-gray-100">
              <span className="text-sm font-medium text-gray-700">
                {plan.contextualTitle || `Floor Plan ${plan.sortOrder + 1}`}
              </span>
              <a
                href={plan.media.url}
                download
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1 text-sm text-primary hover:underline"
              >
                <Download className="w-4 h-4" />
                {download}
              </a>
            </div>
          </div>
        ))}
      </div>

      {/* Fullscreen Modal */}
      {activePlan && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setActivePlan(null)}
        >
          <button
            onClick={() => setActivePlan(null)}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="relative w-full h-full max-w-5xl max-h-[80vh]">
            <Image
              src={activePlan.media.url}
              alt={activePlan.contextualTitle || 'Floor Plan'}
              fill
              className="object-contain"
            />
          </div>
        </div>
      )}
    </section>
  );
}

// =============================================================================
// DOCUMENTS COMPONENT
// =============================================================================

export interface PropertyDocumentsProps {
  documents: MediaLink[];
  className?: string;
  messages?: {
    title?: string;
    subtitle?: string;
    download?: string;
    view?: string;
  };
}

export function PropertyDocuments({
  documents,
  className = '',
  messages = {},
}: PropertyDocumentsProps) {
  const {
    title = 'Property Documents',
    subtitle = 'Download important documents',
    download = 'Download',
    view = 'View',
  } = messages;

  if (documents.length === 0) return null;

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <section className={className}>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileText className="w-6 h-6 text-primary" />
          {title}
        </h2>
        <p className="text-gray-600 mt-1">{subtitle}</p>
      </div>

      <div className="space-y-3">
        {documents.map((doc) => (
          <div
            key={doc.id}
            className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-medium text-gray-900">
                  {doc.contextualTitle || doc.media.originalFilename || doc.media.title}
                </p>
                <p className="text-sm text-gray-500">
                  {doc.media.mimeType} • {formatFileSize(doc.media.sizeBytes || 0)}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <a
                href={doc.media.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                {view}
              </a>
              <a
                href={doc.media.url}
                download
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Download className="w-4 h-4" />
                {download}
              </a>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default PropertyVirtualTour;
